import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, ReplaySubject } from 'rxjs';
import { storageKey } from '../../app.const';
import { OrderDataProvider } from '../data-provider/order.data-provider';
import { ENVIRONMENT_TOKEN } from '../injection.tokens';
import { IVoucher, OrderViewModel } from '../model/view-model/order/order.view.model';
import { PaymentMethodViewModel } from '../model/view-model/order/payment-method/payment-method.view.model';
import { UserCardViewModel } from '../model/view-model/user-history/card/user-card.view.model';
import { UserVoucherViewModel } from '../model/view-model/user-history/voucher/user-voucher.view.model';
import { CountdownComponentService } from '../service/countdown.service';
import { TotalizerService } from '../service/totalizer.service';
import { StateService } from './state.service';
import { distinctUntilChanged, filter, finalize, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { TokenStorageService } from '../service/token-storage.service';
import { CookieService } from 'ngx-cookie-service';
import { PaymentProviderPayMethodRequestModel } from '../model/request/payment-provider-pay-method.request.model';
import { ProviderEnum } from '../enum/provider.enum';
import { CmsGoogleTagManagerService } from '../service/analytics-services/cms-google-tag-manager.service';
import { IOrder } from '../interfaces';
import { ExecutionStrategy } from '../enum/execution-strategy.enum';
import { NotificationService } from '../service/notification/notification.service';
import { TranslationService } from '../service/translation.service';
import { ErrorHandlerService } from '../service/error-handler/error-handler.service';
import { NotificationModel } from '../service/notification/notification.model';
import { NotificationType } from '../service/notification/notification-type.enum';
import { RecalculateOperationType } from '@lib/core';
import { OrderHttpService } from '../http/order.http.service';
import { isEqual } from 'lodash-es';
import { SystemSettingsService } from '../service/system-settings.service';
import { SystemSettingSymbol } from '../model/view-model/system-setting/system-setting-symbol.enum';

@Injectable({
  providedIn: 'root'
})
export class OrderStateService extends StateService {
  private model: OrderViewModel | null;

  private state = new BehaviorSubject<OrderViewModel>(null);
  state$: Observable<OrderViewModel>;

  private initialDataFetched$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  voucher$ = new ReplaySubject<IVoucher[]>();

  private summaryState = new ReplaySubject<void>(1);
  summaryState$: Observable<void>;

  private interceptLoadData = false;
  private initialized = false;

  private _forceTicketsReload: boolean;
  public get getForceTicketsReloadAndResetValue(): boolean {
    const temp = this._forceTicketsReload;
    this._forceTicketsReload = false;
    return temp;
  }
  public set forceTicketsReload(value: boolean) {
    this._forceTicketsReload = value;
  }

  maxExtentionLifetimeCounter = 3;
  orderTtlExtensionsCounter = 0;

  constructor(
    @Inject(ENVIRONMENT_TOKEN) protected environment: any,
    private totalizerService: TotalizerService,
    private orderDataProvider: OrderDataProvider,
    private countdownComponentService: CountdownComponentService,
    protected tokenStorageService: TokenStorageService,
    protected cookieService: CookieService,
    protected cmsGoogleTagManagerService: CmsGoogleTagManagerService,
    protected notificationService: NotificationService,
    protected translationService: TranslationService,
    protected errorHandlerService: ErrorHandlerService,
    private systemSettingsService: SystemSettingsService
  ) {
    super(tokenStorageService, cookieService);

    this.state$ = this.initialDataFetched().pipe(
      switchMap(() => this.state.asObservable().pipe(distinctUntilChanged((prev, curr) => isEqual(prev, curr)))),
      shareReplay(1)
    );

    this.summaryState$ = this.summaryState.asObservable();
    this.initState();
  }

  initialDataFetched() {
    return this.initialDataFetched$.asObservable().pipe(
      filter((v) => !!v),
      take(1)
    );
  }

  private initState() {
    const storedOrder = this.storageReadOrder();
    const initObservable$ = !storedOrder ? of(null) : this.fetchOrderData(storedOrder.cinemaId, storedOrder.id);
    initObservable$.pipe(finalize(() => this.initialDataFetched$.next(true))).subscribe();

    this.orderTtlExtensionsCounter = Number(this.getItem(storageKey.orderTtlExtensionsCounter)) ?? 0;
  }

  storageHasOrder(): boolean {
    return Boolean(this.getItem(storageKey.order));
  }

  storageReadOrder(): IOrder {
    const orderJsonString = this.getItem(storageKey.order);
    return orderJsonString ? (JSON.parse(orderJsonString) as IOrder) : null;
  }

  storageSaveOrder(order: IOrder) {
    if (order) {
      this.setItem(storageKey.order, JSON.stringify({ id: order.id, cinemaId: order.cinemaId }));
    } else {
      this.removeItem(storageKey.order);
      this.removeReservationStorage();
    }
  }

  private fetchOrderData(cinemaId, orderId) {
    return this.orderDataProvider.getOrder(cinemaId, orderId).pipe(
      tap({
        next: (res) => {
          if (!this.interceptLoadData) {
            this.setOrder(res);
          }

          this.interceptLoadData = false;
          this.initialized = true;
        },
        error: (e) => this.removeState()
      })
    );
  }

  getOrder(): OrderViewModel {
    return this.model;
  }

  getOrder$(cinemaId: string, orderId: string) {
    if (cinemaId && orderId && (!this.model || this.model.id != orderId)) {
      OrderHttpService.cacheBuster$.next();
      return this.orderDataProvider.getOrder(cinemaId, orderId);
    }

    return of(this.model);
  }

  getCinemaId(): string {
    return this.model?.cinemaId;
  }

  getPaymentMethod(): PaymentMethodViewModel {
    const lsSelectedPaymentMethod = this.getItem(storageKey.selectedPaymentMethod);
    return lsSelectedPaymentMethod ? JSON.parse(lsSelectedPaymentMethod) : null;
  }

  removeState() {
    this.removeOrder();
  }

  reloadOrder(cinemaId: string, orderId: string) {
    this.orderDataProvider.getOrder(cinemaId, orderId, ExecutionStrategy.FORCE).subscribe((order) => this.setOrder(order));
  }

  setOrder(order: OrderViewModel) {
    this.storageSaveOrder(order);
    if (order !== null) {
      if (this.initialized) {
        this.cmsGoogleTagManagerService.editCart(this.model, order);
      }
    }

    this.model = order;
    // Don't use Object.assign - it does shallow copy and overwrites new screeningItems array with old one
    // Instead, emit the order directly so new prices are propagated
    this.state.next(order);
  }

  setVoucher(number: string, name?: string) {
    if (!number) {
      return;
    }

    const vouchers = this.getVouchers();
    vouchers.push({
      name: name,
      number: number,
      count: [...this.model?.fbItems, ...this.model?.screeningItems].filter((i) => i.hasVoucher()).length
    });
    this.setItem(storageKey.vouchers, JSON.stringify(vouchers));
    this.voucher$.next(vouchers);
  }

  removeVoucher(number: string) {
    if (!number) {
      return;
    }

    const vouchers = this.getVouchers().filter((v) => v.number !== number);
    if (vouchers.length) {
      this.setItem(storageKey.vouchers, JSON.stringify(vouchers));
      this.voucher$.next(vouchers);
    } else {
      this.removeVouchers();
      this.voucher$.next([]);
    }
  }

  getVouchers(): IVoucher[] {
    return (JSON.parse(sessionStorage.getItem(storageKey.vouchers)) as IVoucher[]) || [];
  }

  setPaymentMethod(model: PaymentMethodViewModel) {
    this.setItem(storageKey.selectedPaymentMethod, JSON.stringify(model));
  }

  removeOrder() {
    this.interceptLoadData = true;

    this.clearExtendLifetimeCounter();
    this.totalizerService.clear();

    this.setOrder(null);
  }

  removeOrderFromSessionStorage(): void {
    this.removeFromSessionStorage();
    this.countdownComponentService.destroy();
  }

  cinemaChosen(): boolean {
    return Boolean(this.getItem(storageKey.chosenLocation));
  }

  public useAsPaymentIsDisabled(item?: UserVoucherViewModel | UserCardViewModel): boolean {
    return (
      this.orderNotExist() ||
      this.model?.isRewardsTransaction ||
      item?.isUsed ||
      (item instanceof UserVoucherViewModel && !item?.flgActive) ||
      (item instanceof UserCardViewModel && item?.valueBalance <= 0)
    );
  }

  public assignIsDisabled(card?: UserCardViewModel): boolean {
    return this.orderNotExist() || this.model?.isRewardsTransaction || this.model?.cardId === card.id;
  }

  hasVoucherNumber(voucherNumber: string) {
    return this.model?.hasVoucherNumber(voucherNumber);
  }

  hasCardPayment(cardId: string) {
    return this.model.hasCardPayment(cardId);
  }

  hasOrderTicketById(ticketId: string | null | undefined): boolean {
    if (!ticketId) {
      return false;
    }

    const normalizedTicketId = `${ticketId}`.toLowerCase();
    return this.model?.screeningItems?.some((item) => `${item?.ticketId}`.toLowerCase() === normalizedTicketId) ?? false;
  }

  hasOrderTicketByIds(ticketIds: string[] | null | undefined): boolean {
    if (!ticketIds || ticketIds.length === 0 || !this.model) {
      return false;
    }

    const normalizedTicketIds = ticketIds.map((id) => `${id}`.toLowerCase());
    return this.model?.screeningItems?.some((item) => normalizedTicketIds.includes(`${item?.ticketId}`.toLowerCase())) ?? false;
  }

  hasOrderPromotionById(promotionId: string | null | undefined): boolean {
    if (!promotionId || !this.model) {
      return false;
    }

    const normalizedPromotionId = `${promotionId}`.toLowerCase();

    return [...(this.model.screeningItems ?? []), ...(this.model.fbItems ?? []), ...(this.model.gadgetItems ?? []), ...(this.model.voucherItems ?? [])].some(
      (item) => `${item?.promotionId}`.toLowerCase() === normalizedPromotionId
    );
  }

  public orderNotExist(): boolean {
    return !this.model;
  }

  clearSessionStorage() {
    this.interceptLoadData = true;

    if (this.model?.id && this.model.cinemaId && this.orderDataProvider.silentDelete(this.model)) {
      this.removeOrder();
    }

    this.removeUserSessionStorage();
    this.removeOrderFromSessionStorage();
    this.removeReservationStorage();
  }

  clearSessionStorage$() {
    this.interceptLoadData = true;
    let obs = of(false);

    if (this.model?.id && this.model?.cinemaId) {
      obs = this.orderDataProvider.delete(this.model.cinemaId, this.model?.id);
    }

    return obs.pipe(
      tap(() => {
        this.removeOrder();
        this.removeUserSessionStorage();
        this.removeOrderFromSessionStorage();
        this.removeReservationStorage();
      })
    );
  }

  removeOrderWithDelete(withDestroyCountdown: boolean = true): void {
    if (!this.model?.id || !this.model.cinemaId) {
      return;
    }
    this.orderDataProvider.delete(this.model.cinemaId, this.model.id).subscribe(() => this.removeOrder());

    if (withDestroyCountdown) {
      this.countdownComponentService.destroy();
    }
  }

  removeOrderWithDelete$(withDestroyCountdown: boolean = true): Observable<boolean> {
    if (!this.model?.id || !this.model.cinemaId) {
      return of(false);
    }

    return this.orderDataProvider.delete(this.model.cinemaId, this.model.id).pipe(
      tap(() => {
        if (withDestroyCountdown) {
          this.countdownComponentService.destroy();
        }

        this.removeOrder();
      })
    );
  }

  removeOrderWithRequest(withDestroyCountdown: boolean = true): void {
    if (!this.model?.id || !this.model.cinemaId) {
      return;
    }

    this.orderDataProvider.silentDelete(this.model);
    this.removeOrder();

    if (withDestroyCountdown) {
      this.countdownComponentService.destroy();
    }
  }

  public setLastScreeningId(screeningId: string): void {
    this.setItem(storageKey.lastScreeningId, screeningId);
  }

  public checkLastScreening(screeningId: string): boolean {
    return this.getItem(storageKey.lastScreeningId) !== screeningId;
  }

  public getMemberGetMemberPromotionId() {
    return this.getItem(storageKey.mgmPromotionId);
  }

  public setMemberGetMemberPromotionId(id: string) {
    return this.setItem(storageKey.mgmPromotionId, id);
  }

  public setEmbededPaymentUrl(value: string) {
    this.setItem(storageKey.embededPaymentUrl, value);
  }

  public getEmbededPaymentUrl() {
    return this.getItem(storageKey.embededPaymentUrl);
  }

  public hasVouchersOrGiftCards(): boolean {
    if (this.model) {
      return [...this.model.screeningItems, ...this.model.fbItems].some((x) => x.hasVoucher()) || this.model.paymentMethods.some((x) => x.cardId);
    }

    return false;
  }

  getReservationData() {
    const result = {
      id: this.getItem(storageKey.reservationId),
      cinemaId: this.getItem(storageKey.reservationCinemaId),
      screeningId: this.getItem(storageKey.reservationScreeningId),
      timeTo: DateTime.fromISO(this.getItem(storageKey.reservationScreeningTimeTo))
    };

    return result.id && result.cinemaId ? result : null;
  }

  isEmailFilled() {
    return this.model?.isEmailFilled();
  }

  hasExternalPayments() {
    return this.model?.hasExternalPayments();
  }

  clearExternalPayments(provider: ProviderEnum): Observable<OrderViewModel> {
    if (!this.model?.id || !this.model.cinemaId) {
      return of(null);
    }

    console.log('#clearExternalPayments');

    return this.orderDataProvider.removeExternalPaymentMethod(new PaymentProviderPayMethodRequestModel(this.model.cinemaId, this.model.id, provider)).pipe(
      switchMap(() => this.orderDataProvider.getOrder(this.model.cinemaId, this.model.id)),
      tap({
        next: (o) => {
          this.setOrder(o);
        },
        error: (e) => {}
      })
    );
  }

  changeSummaryStateEmit() {
    this.summaryState.next();
  }

  addErrorNotification(error) {
    const err = this.errorHandlerService.getError(error);
    if (err) {
      if (err.code === '235') {
        err.message = this.translationService.instant(`errors.${err.code}`);
      }

      if (err.properties?.seats?.length > 0 && err.properties.seats.some((s) => [111, 112, 114].includes(s.code))) {
        err.message = this.translationService.instant('errors.orphan-rule');
      }

      if (err.message) {
        this.notificationService.addNotification(new NotificationModel(err.message, NotificationType.ALERT));
      }
    }
  }

  recalculate(operationType: RecalculateOperationType): Observable<OrderViewModel> {
    return this.orderDataProvider.recalculate(this.model.cinemaId, this.model.id, operationType);
  }

  recalculateIfNeeded(order: OrderViewModel, operationType: RecalculateOperationType): Observable<OrderViewModel> {
    return this.systemSettingsService.getSetting$(SystemSettingSymbol.DISCOUNTS_AND_PROMOTIONS_RECALCULATION).pipe(
      switchMap((value) => {
        // Properly handle boolean value from API - could be string "true"/"false", number 1/0, or actual boolean
        const recalculationEnabled = typeof value === 'string' ? value.toLowerCase() === 'true' || value === '1' : !!value;

        if (recalculationEnabled && order.cardId) {
          return this.orderDataProvider.recalculate(order.cinemaId, order.id, operationType);
        }
        return of(order);
      })
    );
  }

  clearExtendLifetimeCounter() {
    this.setorderTtlExtensionsCounter(0);
  }

  setorderTtlExtensionsCounter(value: number) {
    this.setItem(storageKey.orderTtlExtensionsCounter, value.toString());
    this.orderTtlExtensionsCounter = value;
  }

  extendLifetime(seconds: number) {
    const newDateTimeToLive = this.model.dateTimeToLive.plus({ seconds: seconds });
    return this.orderDataProvider.putExtendLifetime(this.model.cinemaId, this.model.id, newDateTimeToLive).pipe(
      switchMap(() => this.orderDataProvider.getOrder(this.model.cinemaId, this.model.id)),
      tap((order) => this.setOrder(order))
    );
  }
}
